import {
  readEffectiveInventorizationContext,
  readEffectiveInventorizationIdentity,
  readEffectiveInventorizationMainReading,
  readEffectiveInventorizationRegisters,
  readEffectiveInventorizationTechnicalDetails,
} from '@/features/inventorization/domain/effective-inventorization-values';
import type { InventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import type { InventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import type { InventorizationPosition } from '@/features/inventorization/domain/inventorization-position';
import { readInventorizationPosition } from '@/features/inventorization/domain/inventorization-position';
import type {
  InventorizationMainReading,
  InventorizationRegisterReading,
} from '@/features/inventorization/domain/inventorization-reading';
import type { InventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import {
  getInventorizationSyncPhotos,
  type InventorizationSyncPhotoRef,
} from '@/features/sync/engine/inventorization-sync-photos';
import { type CaptureSession, WORKFLOW_TYPES } from '@/shared/capture/capture-session';
import { createIdempotencyKey } from '@/shared/db/repositories/sync-queue-repository';

export type { InventorizationSyncPhotoRef } from '@/features/sync/engine/inventorization-sync-photos';

export interface InventorizationSyncFloorplanUpload {
  buildingFloor?: string;
  buildingId?: number | string | null;
  floorplanId?: string;
  localBlobId: string;
  locationId?: number | string;
  mimeType?: string;
  name?: string;
}

export interface InventorizationSyncPayload {
  _id?: string;
  aiExtractionIds: string[];
  context: InventorizationContext;
  id?: number;
  idempotencyKey: string;
  identity: InventorizationIdentity;
  localSessionId: string;
  locationApiKey?: string;
  locationId?: number;
  pendingFloorplan?: InventorizationSyncFloorplanUpload;
  photos: InventorizationSyncPhotoRef[];
  position: InventorizationPosition;
  readings: {
    main: InventorizationMainReading;
    registers: InventorizationRegisterReading[];
  };
  submittedAt?: string;
  submittedBy?: string | null;
  technical: InventorizationTechnicalDetails;
  workflowType: typeof WORKFLOW_TYPES.inventorize;
}

export function mapInventorizationSessionToSyncPayload(
  session: CaptureSession,
  input: { idempotencyKey?: string } = {},
): InventorizationSyncPayload {
  if (session.workflowType !== WORKFLOW_TYPES.inventorize) {
    throw new Error(`Cannot map ${session.workflowType} session as inventorization payload.`);
  }

  const position = readInventorizationPosition(session);
  const technical = readEffectiveInventorizationTechnicalDetails(session);

  return {
    _id: session._id,
    aiExtractionIds: session.aiExtractions.map(extraction => extraction.id),
    context: readEffectiveInventorizationContext(session),
    id: session.meterId,
    idempotencyKey: input.idempotencyKey ?? createIdempotencyKey(session),
    identity: readEffectiveInventorizationIdentity(session),
    localSessionId: session.id,
    locationApiKey: session.locationApiKey,
    locationId: session.locationId,
    pendingFloorplan: position.floorplanBlobId ? { localBlobId: position.floorplanBlobId } : undefined,
    photos: getInventorizationSyncPhotos(session.photos, technical),
    position,
    readings: {
      main: readEffectiveInventorizationMainReading(session),
      registers: readEffectiveInventorizationRegisters(session),
    },
    submittedAt: session.submittedAt,
    submittedBy: session.submittedBy ?? session.createdBy ?? null,
    technical,
    workflowType: WORKFLOW_TYPES.inventorize,
  };
}
